const DEFAULT_FRONTDESK_REQUEST_TIMEOUT_MS = 20_000;

type FetchPreconnect = (url: unknown, options?: unknown) => void;

/**
 * Fetch implementation accepted by Effect's browser HTTP client.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const bridgeFetch: FrontdeskFetch = createBoundedFetch(globalThis.fetch);
 * ```
 */
export type FrontdeskFetch = typeof globalThis.fetch & {
  preconnect: FetchPreconnect;
};

class FrontdeskRequestTimeoutError extends Error {
  constructor(timeoutMs: number, cause: unknown) {
    super(`FrontDesk RPC request timed out after ${timeoutMs} ms`, { cause });
    this.name = "FrontdeskRequestTimeoutError";
  }
}

function preconnectWithNativeFetch(url: unknown, options?: unknown): void {
  const nativePreconnect = Reflect.get(globalThis.fetch, "preconnect");
  if (typeof nativePreconnect === "function") {
    Reflect.apply(nativePreconnect, globalThis.fetch, [url, options]);
  }
}

/**
 * Bounds headers and response-body consumption while preserving caller cancellation.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const fetchWithTimeout = createBoundedFetch(globalThis.fetch, 10_000);
 * await fetchWithTimeout("https://example.test/status");
 * ```
 */
export function createBoundedFetch(
  baseFetch: typeof globalThis.fetch,
  timeoutMs = DEFAULT_FRONTDESK_REQUEST_TIMEOUT_MS,
): FrontdeskFetch {
  const boundedFetch = async (
    ...args: Parameters<typeof globalThis.fetch>
  ): Promise<Response> => {
    const [input, init] = args;
    const controller = new AbortController();
    const callerSignal =
      init?.signal === undefined
        ? input instanceof Request
          ? input.signal
          : undefined
        : init.signal;
    let abortBody: ((reason: unknown) => void) | undefined;
    let rejectRequest: ((reason: unknown) => void) | undefined;
    const abortFromCaller = () => controller.abort(callerSignal?.reason);
    const timeout = globalThis.setTimeout(() => {
      controller.abort(new FrontdeskRequestTimeoutError(timeoutMs, undefined));
    }, timeoutMs);
    const cleanup = () => {
      globalThis.clearTimeout(timeout);
      callerSignal?.removeEventListener("abort", abortFromCaller);
      controller.signal.removeEventListener("abort", abort);
    };
    const abort = () => {
      rejectRequest?.(controller.signal.reason);
      abortBody?.(controller.signal.reason);
      cleanup();
    };

    // Native HTTP may ignore signals, so cancellation must also settle the caller.
    const response = await new Promise<Response>((resolve, reject) => {
      rejectRequest = reject;
      controller.signal.addEventListener("abort", abort, { once: true });
      if (callerSignal?.aborted) {
        abortFromCaller();
        return;
      }
      callerSignal?.addEventListener("abort", abortFromCaller, { once: true });
      Promise.resolve()
        .then(() => {
          controller.signal.throwIfAborted();
          return baseFetch(input, { ...init, signal: controller.signal });
        })
        .then(
          (result) => {
            if (controller.signal.aborted) {
              void result.body
                ?.cancel(controller.signal.reason)
                .catch(() => undefined);
              return undefined;
            }
            return resolve(result);
          },
          (error: unknown) => {
            cleanup();
            return reject(error);
          },
        );
    });

    if (!response.body) {
      cleanup();
      return response;
    }

    const reader = response.body.getReader();
    let finished = false;
    const body = new ReadableStream<Uint8Array>(
      {
        start(stream) {
          abortBody = (reason) => {
            if (finished) return;
            finished = true;
            stream.error(reason);
            void reader.cancel(reason).catch(() => undefined);
          };
          if (controller.signal.aborted) abortBody(controller.signal.reason);
        },
        async pull(stream) {
          try {
            const chunk = await reader.read();
            if (finished) return;
            if (chunk.done) {
              finished = true;
              cleanup();
              reader.releaseLock();
              stream.close();
            } else {
              stream.enqueue(chunk.value);
            }
          } catch (error) {
            if (finished) return;
            finished = true;
            cleanup();
            reader.releaseLock();
            stream.error(error);
          }
        },
        cancel(reason) {
          finished = true;
          cleanup();
          controller.abort(reason);
          return reader.cancel(reason);
        },
      },
      { highWaterMark: 0 },
    );

    // Keep the deadline alive after headers without buffering the RPC stream.
    const boundedResponse = new Response(body, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    });
    Object.defineProperties(boundedResponse, {
      url: { value: response.url },
      redirected: { value: response.redirected },
      type: { value: response.type },
    });
    return boundedResponse;
  };

  return Object.assign(boundedFetch, {
    preconnect: preconnectWithNativeFetch,
  });
}
